// ============================================================
// MANIC MEETER — Shop (spend Impostor Points)
// ============================================================

import {
  MetaProgression,
  StatUpgradeId, PowerupId,
  PermanentUnlockId,
  VendorEncounter,
  GameState,
} from "./types";
import { permanentUnlocks, powerupDefs, statUpgrades } from "./content";
import { C } from "./i18n";
import {
  getUpgradeCost, canAffordUpgrade, purchaseUpgrade,
  canAffordPowerup, purchasePowerup,
  canAffordPermanentUnlock, purchasePermanentUnlock,
} from "./persistence";

/**
 * Show the shop overlay. Returns a Promise that resolves when the player closes it.
 */
export function showShop(meta: MetaProgression): Promise<void> {
  return new Promise((resolve) => {
    const overlay = document.createElement("div");
    overlay.className = "shop-overlay";

    const panel = document.createElement("div");
    panel.className = "shop-panel";

    function closeShop() {
      overlay.remove();
      resolve();
    }

    function render() {
      panel.innerHTML = "";
      const ui = C().ui;

      // Header with X close button
      const header = document.createElement("div");
      header.className = "shop-header";
      header.innerHTML = `
        <button class="shop-x-btn" title="${ui.actions.close}">✕</button>
        <h2>${ui.shop.heading}</h2>
        <div class="shop-points">🎭 <strong>${meta.impostorPoints}</strong> ${ui.shop.points}</div>
      `;
      header.querySelector(".shop-x-btn")!.addEventListener("click", closeShop);
      panel.appendChild(header);

      // Split stat upgrades into active (not maxed) and completed (maxed)
      const upgrades = statUpgrades();
      const activeStats = upgrades.filter(u => meta.statLevels[u.id] < u.maxLevel);
      const completedStats = upgrades.filter(u => meta.statLevels[u.id] >= u.maxLevel);

      // Active Stat Upgrades section (only if there are any left)
      if (activeStats.length > 0) {
        const statsSection = document.createElement("div");
        statsSection.className = "shop-section";
        statsSection.innerHTML = `<h3>${ui.shop.statUpgrades}</h3>`;

        for (const upg of activeStats) {
          const level = meta.statLevels[upg.id];
          const cost = getUpgradeCost(upg.id, level);
          const canBuy = canAffordUpgrade(meta, upg.id);

          const row = document.createElement("div");
          row.className = "shop-item" + (!canBuy ? " too-expensive" : "");
          row.innerHTML = `
            <div class="shop-item-info">
              <span class="shop-item-name">${upg.name}</span>
              <span class="shop-item-desc">${upg.description}</span>
              <span class="shop-item-level">${ui.shop.level(level, upg.maxLevel)}</span>
            </div>
            <div class="shop-item-action">
              <button class="shop-buy-btn" data-type="stat" data-id="${upg.id}" ${!canBuy ? "disabled" : ""}>${cost} 🎭</button>
            </div>
          `;
          statsSection.appendChild(row);
        }
        panel.appendChild(statsSection);
      }

      // Powerups section
      const powerSection = document.createElement("div");
      powerSection.className = "shop-section";
      powerSection.innerHTML = `<h3>${ui.shop.powerups}</h3>`;

      for (const pw of powerupDefs()) {
        const owned = meta.powerupInventory[pw.id];
        const canBuy = canAffordPowerup(meta, pw.id);
        const usableTag = pw.usableInPrep && pw.usableInMeeting ? ui.shop.usableBoth
          : pw.usableInPrep ? ui.shop.usablePrep
          : ui.shop.usableMeeting;

        const row = document.createElement("div");
        row.className = "shop-item" + (!canBuy ? " too-expensive" : "");
        row.innerHTML = `
          <div class="shop-item-info">
            <span class="shop-item-name">${pw.name}</span>
            <span class="shop-item-desc">${pw.description}</span>
            <span class="shop-item-meta">${ui.shop.owned(owned)} | ${ui.shop.usableLabel}: ${usableTag}</span>
          </div>
          <div class="shop-item-action">
            <button class="shop-buy-btn" data-type="powerup" data-id="${pw.id}" ${!canBuy ? "disabled" : ""}>${pw.cost} 🎭</button>
          </div>
        `;
        powerSection.appendChild(row);
      }
      panel.appendChild(powerSection);

      // Permanent Unlocks section
      const unlockSection = document.createElement("div");
      unlockSection.className = "shop-section";
      unlockSection.innerHTML = `<h3>${ui.shop.permanentUnlocks}</h3><p class="shop-section-hint">${ui.shop.permanentHint}</p>`;

      for (const unlock of permanentUnlocks()) {
        const owned = meta.permanentUnlocks[unlock.id];
        const canUnlock = canAffordPermanentUnlock(meta, unlock.id);

        const row = document.createElement("div");
        row.className = "shop-item" + (owned ? " maxed" : "") + (!canUnlock && !owned ? " too-expensive" : "");
        row.innerHTML = `
          <div class="shop-item-info">
            <span class="shop-item-name">${unlock.emoji} ${unlock.name}</span>
            <span class="shop-item-desc">${unlock.description}</span>
            <span class="shop-item-meta">${ui.shop.effect}: ${unlock.effect}</span>
          </div>
          <div class="shop-item-action">
            ${owned
              ? '<span class="shop-maxed">✅</span>'
              : `<button class="shop-buy-btn" data-type="unlock" data-id="${unlock.id}" ${!canUnlock ? "disabled" : ""}>${ui.shop.unlockFrom(unlock.cost)}</button>`
            }
          </div>
        `;
        unlockSection.appendChild(row);
      }
      panel.appendChild(unlockSection);

      // Completed stat upgrades section at the bottom
      if (completedStats.length > 0) {
        const doneSection = document.createElement("div");
        doneSection.className = "shop-section shop-section-completed";
        doneSection.innerHTML = `<h3>${ui.shop.completed}</h3>`;

        for (const upg of completedStats) {
          const row = document.createElement("div");
          row.className = "shop-item maxed";
          row.innerHTML = `
            <div class="shop-item-info">
              <span class="shop-item-name">${upg.name}</span>
              <span class="shop-item-desc">${upg.description}</span>
              <span class="shop-item-level">${ui.shop.level(upg.maxLevel, upg.maxLevel)}</span>
            </div>
            <div class="shop-item-action">
              <span class="shop-maxed">${ui.shop.maxed}</span>
            </div>
          `;
          doneSection.appendChild(row);
        }
        panel.appendChild(doneSection);
      }

      // Close button
      const closeBtn = document.createElement("button");
      closeBtn.className = "shop-close-btn";
      closeBtn.textContent = ui.shop.close;
      closeBtn.addEventListener("click", closeShop);
      panel.appendChild(closeBtn);

      // Buy handlers
      panel.querySelectorAll(".shop-buy-btn").forEach((btn) => {
        btn.addEventListener("click", () => {
          const el = btn as HTMLButtonElement;
          const type = el.dataset.type;
          const id = el.dataset.id!;
          if (type === "stat") {
            purchaseUpgrade(meta, id as StatUpgradeId);
          } else if (type === "unlock") {
            purchasePermanentUnlock(meta, id as PermanentUnlockId);
          } else {
            purchasePowerup(meta, id as PowerupId);
          }
          render(); // re-render after purchase
        });
      });
    }

    render();
    overlay.appendChild(panel);
    document.body.appendChild(overlay);
  });
}

/**
 * Show a powerup selection dialog during prep or meeting.
 * Only shows powerups matching the given usage context.
 * Returns the selected PowerupId or null if the player skips.
 */
export function showPowerupSelect(
  meta: MetaProgression,
  context: "prep" | "meeting",
): Promise<PowerupId | null> {
  return new Promise((resolve) => {
    // Filter available powerups
    const ui = C().ui;
    const available = powerupDefs().filter((pw) => {
      if (meta.powerupInventory[pw.id] <= 0) return false;
      return context === "prep" ? pw.usableInPrep : pw.usableInMeeting;
    });

    if (available.length === 0) {
      resolve(null);
      return;
    }

    const overlay = document.createElement("div");
    overlay.className = "shop-overlay powerup-select-overlay";

    const panel = document.createElement("div");
    panel.className = "shop-panel powerup-select-panel";

    panel.innerHTML = `<h3>${ui.powerups.useHeading}</h3>`;

    for (const pw of available) {
      const btn = document.createElement("button");
      btn.className = "shop-buy-btn powerup-use-btn";
      btn.innerHTML = `${pw.name} <span class="shop-item-desc">${pw.description}</span> ${ui.powerups.inventoryCount(meta.powerupInventory[pw.id])}`;
      btn.addEventListener("click", () => {
        overlay.remove();
        resolve(pw.id);
      });
      panel.appendChild(btn);
    }

    const skipBtn = document.createElement("button");
    skipBtn.className = "shop-close-btn";
    skipBtn.textContent = ui.actions.skip;
    skipBtn.addEventListener("click", () => {
      overlay.remove();
      resolve(null);
    });
    panel.appendChild(skipBtn);

    overlay.appendChild(panel);
    document.body.appendChild(overlay);
  });
}

/**
 * Show a vendor encounter shop where items are bought with salary.
 * Returns list of purchased powerup IDs.
 */
export function showVendorShop(
  vendor: VendorEncounter,
  state: GameState,
): Promise<PowerupId[]> {
  return new Promise((resolve) => {
    const purchased: PowerupId[] = [];

    const overlay = document.createElement("div");
    overlay.className = "shop-overlay vendor-overlay";

    const panel = document.createElement("div");
    panel.className = "shop-panel vendor-panel";

    function render() {
      const ui = C().ui;
      panel.innerHTML = "";
      panel.innerHTML += `
        <h2>${vendor.emoji} ${vendor.name}</h2>
        <p class="vendor-intro">${vendor.intro}</p>
        <div class="shop-points">${ui.vendor.yourSalary}: <strong>${state.player.salary}€</strong></div>
      `;

      for (const item of vendor.items) {
        const canBuy = state.player.salary >= item.salaryCost;
        const row = document.createElement("div");
        row.className = "shop-item" + (!canBuy ? " too-expensive" : "");
        row.innerHTML = `
          <div class="shop-item-info">
            <span class="shop-item-name">${item.label}</span>
          </div>
          <div class="shop-item-action">
            <button class="shop-buy-btn vendor-buy-btn" data-id="${item.powerupId}" data-cost="${item.salaryCost}" ${!canBuy ? "disabled" : ""}>${item.salaryCost}€</button>
          </div>
        `;
        panel.appendChild(row);
      }

      const closeBtn = document.createElement("button");
      closeBtn.className = "shop-close-btn";
      closeBtn.textContent = ui.vendor.walkOn;
      closeBtn.addEventListener("click", () => {
        overlay.remove();
        resolve(purchased);
      });
      panel.appendChild(closeBtn);

      panel.querySelectorAll(".vendor-buy-btn").forEach((btn) => {
        btn.addEventListener("click", () => {
          const el = btn as HTMLButtonElement;
          const pwId = el.dataset.id as PowerupId;
          const cost = parseInt(el.dataset.cost!);
          if (state.player.salary >= cost) {
            state.player.salary -= cost;
            state.meta.powerupInventory[pwId]++;
            purchased.push(pwId);
            render();
          }
        });
      });
    }

    render();
    overlay.appendChild(panel);
    document.body.appendChild(overlay);
  });
}
